<template>
  <sdf-focus-pane
    v-if="mount"
    v-bind="webComponentProps"
    :visible="show"
    @sdfAccept="$emit('sdf-accept', $event)"
    @sdfDismiss="onDismiss"
    @sdfAfterOpen="$emit('sdf-after-open', $event)"
    @sdfAfterClose="onAfterClose"
  >
    <SdfAlertBanner :show="!!errorMsg" status="error">{{ errorMsg }}</SdfAlertBanner>

    <template v-for="slotName in sdfFocusPaneSlotNames" :key="slotName">
      <div
        v-if="$slots[slotName]"
        :slot="slotName !== 'default' ? slotName : null"
        :class="[
          slotName === 'default' && errorMsg
            ? 'mt-4'
            : slotName === 'default'
              ? 'flex h-full flex-col pb-1 pt-1'
              : 'contents',
          { 'pointer-events-none opacity-50': slotName === 'default' && disabled },
        ]"
      >
        <slot :name="slotName"></slot>
      </div>
    </template>
  </sdf-focus-pane>
</template>

<script setup lang="ts">
import { useWebComponentProps } from '@shared/composables/useWebComponentProps';
import SdfAlertBanner from '@shared/ui/sdf-alert-banner/sdf-alert-banner.vue';
import { sdfFocusPaneSlotNames, type SdfFocusPaneSlots } from '@shared/ui/types/slots';
import type { JSX, SdfFocusPaneCustomEvent } from '@waypoint/web-components';
import { computed, nextTick, ref, watch } from 'vue';
import { useSdfFocusPane } from './useSdfFocusPane';

defineOptions({
  name: 'SdfFocusPane',
});

defineSlots<SdfFocusPaneSlots>();

type Props = Partial<JSX.SdfFocusPane> & {
  /**
   * The name identifier for the focus pane component
   */
  name?: string;
  /**
   * The v-model binding for controlling the visibility of the focus pane
   */
  modelValue?: boolean;
  /**
   * An optional error message to display in the focus pane
   */
  errorMsg?: string;
  /**
   * Whether the focus pane content is disabled.
   * Prevents user interaction with focus pane content.
   */
  disabled?: boolean;
  /**
   * When true, keeps the component mounted in the DOM even when closed.
   * Useful for preserving form state across open/close cycles.
   */
  live?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  name: undefined,
  modelValue: false,
  errorMsg: undefined,
  disabled: false,
  live: false,
  closeable: true,
  portalEnabled: true,
  paneType: 'floating',
  size: 'sm',
  spacing: 'loose',
  hideAcceptButton: true,
  hideDismissButton: true,
});

const webComponentProps = useWebComponentProps(props, {
  exclude: ['name', 'modelValue', 'errorMsg', 'disabled', 'live'],
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'sdf-accept': [event: SdfFocusPaneCustomEvent<void>];
  'sdf-dismiss': [event: SdfFocusPaneCustomEvent<void>];
  'sdf-after-open': [event: SdfFocusPaneCustomEvent<void>];
  'sdf-after-close': [event: SdfFocusPaneCustomEvent<void>];
}>();

const { focusPanes, hide } = useSdfFocusPane();

const isVisible = computed(() => {
  if (props.name) {
    return focusPanes.value[props.name] ?? false;
  }

  return props.modelValue;
});

const mount = ref(props.live);
const show = ref(false);

watch(
  isVisible,
  (newVal) => {
    if (newVal) {
      mount.value = true;
      nextTick(() => {
        show.value = true;
      });
    } else {
      show.value = false;
    }
  },
  { immediate: true }
);

const onAfterClose = (event: SdfFocusPaneCustomEvent<void>) => {
  if (!props.live) {
    mount.value = false;
  }
  emit('sdf-after-close', event);
};

const onDismiss = (event: SdfFocusPaneCustomEvent<void>) => {
  if (props.disabled) {
    return;
  }
  emit('sdf-dismiss', event);
  if (props.name) {
    hide(props.name);
  } else {
    emit('update:modelValue', false);
  }
};
</script>
